import React from 'react'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import PhoneInput from 'react-phone-input-2'
import clsx from 'clsx'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCaretLeft} from '@fortawesome/free-solid-svg-icons'
import {getLogger} from '../../services/logging'
import {GuestInfo, GuestInfoSchema, User} from '../../schema'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('CheckoutGuestInfoForm')

export interface CheckoutGuestInfoFormProps {
  className?: string
  isEditMode: boolean
  guestInfo: GuestInfo
  user: User
  onBack: () => void
  onSave: (guestInfo: GuestInfo) => void
  onCancel: () => void
}

const CheckoutGuestInfoForm = ({
  className = '',
  isEditMode,
  guestInfo,
  user,
  onBack,
  onSave,
  onCancel
}: CheckoutGuestInfoFormProps) => {
  const [formattedPhone, setFormattedPhone] = React.useState<string>('')
  const {register, handleSubmit, control, formState, watch} = useForm<
    GuestInfo
  >({
    defaultValues: {
      firstName: guestInfo?.firstName || user?.firstName || '',
      lastName: guestInfo?.lastName || user?.lastName || '',
      phone: guestInfo?.phone || user?.phone || '',
      email: guestInfo?.email || user?.email || '',
      comments: guestInfo?.comments || '',
      acceptTerms: guestInfo?.acceptTerms || false
    },
    resolver: zodResolver(GuestInfoSchema)
  })

  // check if form is valid
  const isFormValid =
    watch('firstName') &&
    watch('lastName') &&
    watch('phone') &&
    watch('email') &&
    watch('acceptTerms')

  // check if form values have changed
  const hasGuestInfoChanged =
    guestInfo?.firstName !== watch('firstName') ||
    guestInfo?.lastName !== watch('lastName') ||
    guestInfo?.phone !== watch('phone') ||
    guestInfo?.email !== watch('email') ||
    guestInfo?.comments !== watch('comments') ||
    guestInfo?.acceptTerms !== watch('acceptTerms')

  const {errors} = formState

  return (
    <form
      className={clsx('flex flex-col gap-1', className)}
      onSubmit={handleSubmit((guestInfo: GuestInfo) => {
        logger.info({message: 'Guest info form submitted', data: {guestInfo}})
        pushDataLayerEvent({
          event: 'Checkout_Guest_Info_Submit',
          category: 'IBE',
          value: 1
        })
        onSave({
          firstName: guestInfo.firstName,
          lastName: guestInfo.lastName,
          email: guestInfo.email,
          phone: guestInfo.phone,
          comments: guestInfo.comments,
          acceptTerms: guestInfo.acceptTerms,
          formattedPhone,
          lastUpdated: new Date().toISOString()
        })
      })}
      aria-label="Guest information form."
    >
      <p className="mb-2">Please provide your contact information.</p>
      <label className="form-control">
        <div className="label">
          <span className="label-text">First Name*</span>
        </div>
        <input
          type="text"
          className="input input-bordered input-sm"
          aria-label="First name input field."
          aria-required="true"
          {...register('firstName')}
        />
        {errors.firstName ? (
          <div className="label">
            <span className="label-text-alt text-error">
              {errors.firstName.message}
            </span>
          </div>
        ) : (
          ''
        )}
      </label>
      <label className="form-control">
        <div className="label">
          <span className="label-text">Last Name*</span>
        </div>
        <input
          type="text"
          className="input input-bordered input-sm"
          aria-label="Last name input field."
          aria-required="true"
          {...register('lastName')}
        />
        {errors.lastName ? (
          <div className="label">
            <span className="label-text-alt text-error">
              {errors.lastName.message}
            </span>
          </div>
        ) : (
          ''
        )}
      </label>
      <label className="form-control">
        <div className="label">
          <span className="label-text">Mobile Phone*</span>
        </div>
        <Controller
          name="phone"
          control={control}
          render={({field}: any) => {
            return (
              <PhoneInput
                country={'us'}
                value={field.value}
                onChange={(
                  value: string,
                  _country: any,
                  _e: any,
                  formattedValue: string
                ) => {
                  // update phone field
                  field.onChange(value)
                  setFormattedPhone(formattedValue)
                }}
              />
            )
          }}
          aria-label="Phone number input field."
          aria-required="true"
        />

        {errors.phone ? (
          <div className="label">
            <span className="label-text-alt text-error">
              {errors.phone.message}
            </span>
          </div>
        ) : (
          ''
        )}
      </label>
      <label className="form-control">
        <div className="label">
          <span className="label-text">Email*</span>
        </div>
        <input
          type="text"
          className="input input-bordered input-sm"
          aria-label="Email input field."
          aria-required="true"
          {...register('email')}
        />
        {errors.email ? (
          <div className="label">
            <span className="label-text-alt text-error">
              {errors.email.message}
            </span>
          </div>
        ) : (
          ''
        )}
      </label>
      <label className="form-control">
        <div className="label">
          <span className="label-text">Comments about your stay</span>
        </div>
        <textarea
          className="textarea textarea-bordered"
          aria-label="Comments about your stay."
          aria-required="false"
          {...register('comments')}
        ></textarea>
      </label>

      <label className="form-control">
        <div className="label justify-start">
          <input
            type="checkbox"
            className="checkbox mr-2"
            {...register('acceptTerms')}
          />
          <span className="label-text">
            I accept Placemakr's{' '}
            <a
              className="link text-accent text-bold no-underline hover:underline"
              href="https://www.placemakr.com/terms-and-conditions"
              target="_blank"
              onClick={e => {
                e.stopPropagation()
              }}
              aria-label="View Terms and Conditions."
            >
              Terms and Conditions
            </a>
            .
          </span>
        </div>
        {errors.acceptTerms ? (
          <div className="label">
            <span className="label-text-alt text-error">
              Terms and Conditions must be accepted.
            </span>
          </div>
        ) : (
          ''
        )}
      </label>

      <p className="text-left text-xs text-gray-500">
        By submitting your mobile phone number and checking the box, you consent
        to receive text messages from Placemakr regarding your stay. Message
        frequency will vary.{' '}
        <a
          className="link text-cabana text-bold no-underline hover:underline"
          href="https://www.placemakr.com/terms-and-conditions"
          target="_blank"
          aria-label="SMS Terms/Privacy Policy."
        >
          SMS Terms/Privacy Policy
        </a>
        . Message and data rates may apply. Reply STOP to opt out, Reply HELP
        for Help.
      </p>

      {isEditMode ? (
        <div className="flex justify-center gap-2 w-full mt-2">
          <button
            className="btn btn-ghost mt-4"
            onClick={() => {
              pushDataLayerEvent({
                event: 'Checkout_Guest_Info_Cancel',
                category: 'IBE',
                value: 1
              })
              onCancel()
            }}
            aria-label="Cancel editing guest information."
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-secondary mt-4"
            aria-label="Update guest information."
            disabled={!isFormValid || !hasGuestInfoChanged}
          >
            Update
          </button>
        </div>
      ) : (
        <button
          type="submit"
          className="btn btn-secondary mt-4"
          aria-label="Continue to payment."
        >
          continue
        </button>
      )}

      {!user.isAuthenticated && !isEditMode && (
        <div className="flex justify-center w-full mt-2">
          <a
            className="link link-primary text-sm"
            onClick={() => {
              onBack()
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
              if (e.key === 'Enter') {
                onBack()
              }
            }}
            tabIndex={0}
            aria-label="Back to choose guest information view."
          >
            <FontAwesomeIcon
              icon={faCaretLeft}
              className="mr-1"
              title="Back to guest info"
            />
            <span>back</span>
          </a>
        </div>
      )}
    </form>
  )
}

export default CheckoutGuestInfoForm
